import { useEffect, useRef, useState, type CSSProperties } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import type { RoomState } from "../gen/bazivan/v1/table_pb";
import { fa, faNum } from "../game/cards";
import { avatarSrc } from "../game/avatar";
import { BotIcon } from "./BotIcon";
import { botBare } from "../game/seats";
import { placeLabel } from "../game/place";
import { roomsOnly } from "./games";
import { menchEffective, menchPill, menchRulesFromRoom } from "./menchRules";
import { MenchPiecePicker } from "./MenchPiecePicker";
import { SCENARIOS } from "../mafia/data";
import { arrowsRoomFrom, arrowsRoomSummary } from "../arrows/RoomPicker";
import { HANDMADE } from "../arrows/stages";
import { Thumb } from "../arrows/Thumb";
import { PlayerSheet, type SheetPlayer } from "./PlayerSheet";
import { blocked } from "../net/safety";
import { TablePicker, hasTables, loadTableChoice, saveTableChoice, tableName } from "./TablePicker";
import { CountryFlag } from "./CountryFlag";

/** اتاقِ خصوصی: کد را می‌دهی، دوستت با همان کد می‌آید. */
export function Room({ room }: { room: RoomState }) {
  const myId = useSignal(client.playerId);
  const blockedSet = useSignal(blocked);
  const [pick, setPick] = useState<SheetPlayer | null>(null);
  const host = room.hostId === myId;
  const filled = room.seats.filter((s) => s.playerId).length;
  // تخته‌ای‌ها با آدم‌های نشسته و ربات‌هایی شروع می‌شوند که میزبان روی صندلیِ خالی نشانده (Seat.bot بی player_id —
  // match.Rooms.SetBot)؛ صندلیِ خالیِ دیگر ربات نمی‌گیرد، و میزبانِ تنها که رباتی ننشانده یک ربات می‌گیرد
  // (match.boardPlayers). مافیا مثلِ ورق‌ها: سناریو شمارِ معینی بازیکن می‌خواهد و هر جای خالی ربات می‌شود (mafia.FromRoom).
  const board = roomsOnly(room.game) && room.game !== "mafia";
  const seatedBots = room.seats.filter((s) => !s.playerId && s.bot).length;
  const bots = board ? Math.max(seatedBots, 2 - filled) : room.seats.length - filled;
  // سرِ میز چند نفر می‌نشینند — تیمیِ منچ و قرصِ قانون‌ها با همین (ربات‌ها هم بازیکن‌اند)
  const players = board ? filled + bots : room.seats.length;
  // ربات‌نشاندن: میزبانِ تخته‌ایِ بیش از دونفره (دوز و تخته‌نرد دونفره‌اند و میزبانِ تنها خودش یک ربات می‌گیرد)
  const seatBots = host && board && room.seats.length > 2;
  // منچ: قانون‌های میزبان. کلیدِ start نباشد = سرورِ قدیمی = کلاسیک (نه پیش‌فرضِ تازه)
  const mn = room.game === "mench" ? menchRulesFromRoom(room.rules) : null;
  // آدمک: منچ و مارپله (کاربر، ۲۵ اکتبر ۲۰۲۶) — هر نشسته مالِ خودش را انتخاب می‌کند
  const pieces = room.game === "mench" || room.game === "marpele";
  // تیمی فقط با چهار نفر سرِ میز (آدم یا رباتِ میزبان)؛ ترتیبِ بازی همان ترتیبِ صندلی است: ۱ و ۳ یک تیم، ۲ و ۴ تیمِ دیگر
  const teams = ((!!mn && mn.teams === 1) || (room.game === "arrows" && room.rules["teams"] === 1 && room.rules["mode"] !== 1)) && players === 4;
  const mine = room.seats.find((s) => s.playerId && s.playerId === myId);
  const partner = teams && mine ? room.seats.find((s) => s.index === (mine.index + 2) % 4) : undefined;

  return (
    <div className="page stack">
      <div className="plate" style={{ textAlign: "center" }}>
        <div className="muted">کد اتاق</div>
        <div style={{ fontSize: 40, fontWeight: 700, letterSpacing: 10, color: "var(--gold-bright)", direction: "ltr" }}>
          {room.code}
        </div>
        <ShareTable code={room.code} />
      </div>

      <div className="plate stack">
        {room.seats.map((s) => {
          // هم‌اتاقیِ آدم (نه خودم، نه ربات): لمسش برگه‌ی بازیکن را باز می‌کند — بلاک یا گزارش.
          const other = !!s.playerId && !s.bot && s.playerId !== myId;
          const botSeat = !s.playerId && s.bot;
          // صندلیِ خالی یا رباتیِ میزبان خودش دکمه است: لمس = ربات بنشیند، لمسِ دوباره = برود
          if (!s.playerId && seatBots) {
            return (
              <button key={s.index} className={`row seatrow${botSeat ? " on" : ""}`} aria-pressed={botSeat}
                      aria-label={botSeat ? `برداشتن ربات از صندلی ${fa(s.index + 1)}` : `نشاندن ربات روی صندلی ${fa(s.index + 1)}`}
                      onClick={() => client.setRoomBot(s.index, !botSeat)}>
                <span className="row" style={{ gap: 10 }}>
                  <BotSeatFace bot={botSeat} host />
                  {botSeat ? <span>حریف</span> : <span className="muted">— خالی —</span>}
                </span>
                <span className="muted">صندلی {fa(s.index + 1)}</span>
              </button>
            );
          }
          if (botSeat) {
            return (
              <div key={s.index} className="row" style={{ justifyContent: "space-between" }}>
                <span className="row" style={{ gap: 10 }}>
                  <BotSeatFace bot host={false} />
                  <span>حریف</span>
                </span>
                <span className="muted">صندلی {fa(s.index + 1)}</span>
              </div>
            );
          }
          return (
          <div key={s.index} className="row" style={{ justifyContent: "space-between" }}>
            <span className="row" style={{ gap: 10 }}>
              {s.playerId
                ? <span className="cflag-host"><img className="seatface" src={avatarSrc(s.avatar)} alt="" width={34} height={34} /><CountryFlag code={s.country} /></span>
                : <span className="seatface empty" />}
              <span className="row" style={{ gap: 6 }}>
                {s.playerId && s.bot && <BotIcon />}
                {other ? (
                  <button
                    className="seatwho"
                    title="بلاک یا گزارش"
                    onClick={() => setPick({ id: s.playerId, name: s.name, avatar: s.avatar, country: s.country })}
                  >
                    {s.name || "بازیکن"}
                    {blockedSet.has(s.playerId) && <span className="tag">بلاک‌شده</span>}
                  </button>
                ) : s.playerId ? (botBare(s.name, s.bot) || "بازیکن") : "— خالی —"}
                {s.playerId && placeLabel(s) && <span className="plc-tag">{placeLabel(s)}</span>}
                {/* منچ و مارپله: آدمکِ هر نشسته، همان که سرور دارد — انتخابِ «آدمکِ من» این‌جا دیده می‌شود */}
                {pieces && s.playerId && (
                  <img className="rm-piece" src={`/art/mench/${s.piece === 1 ? "g" : "p"}0-idle.webp`} alt="" width={18} height={24} />
                )}
                {teams && s.playerId && <i className={`mn-team big t${s.index % 2}`}>تیم {fa((s.index % 2) + 1)}</i>}
              </span>
            </span>
            <span className="muted">صندلی {fa(s.index + 1)}</span>
          </div>
          );
        })}
        {/* میزبانِ تخته‌ای: چند ربات بیاید دستِ خودش است — پیش‌فرض همان «فقط آدم‌ها» (board-rooms-no-bot-fill) */}
        {seatBots && (
          <div className="muted roombots" role="status">
            {seatedBots > 0
              ? `${filled <= 1 ? `میز با تو و ${fa(seatedBots)} ربات` : `میز با ${fa(filled)} نفر و ${fa(seatedBots)} ربات`} شروع می‌شود. دوستی که بیاید جای ربات را می‌گیرد؛ برای برداشتن ربات دوباره رویش بزن.`
              : filled < 2
                ? "تنها شروع کنی، یک ربات حریفت می‌شود. حریف بیشتر می‌خواهی؟ روی صندلی خالی بزن تا ربات بنشیند."
                : `با همین ${fa(filled)} نفر شروع می‌شود؛ صندلی خالی ربات نمی‌گیرد، مگر خودت رویش بزنی.`}
          </div>
        )}
      </div>
      {pick && <PlayerSheet player={pick} context="room" onClose={() => setPick(null)} />}

      {/* آدمکِ منچ و مارپله: سلیقه‌ی خودِ هر بازیکن، نه انتخابِ میزبان — مهمانی که با لینک آمده هم این‌جا انتخابش می‌کند */}
      {pieces && mine && (
        <div className="plate">
          <MenchPiecePicker />
        </div>
      )}

      {/* طرحِ میز: انتخابِ میزبان («قرعه» = سرور سرِ شروع برمی‌دارد) — همه می‌بینند، میزبان عوضش می‌کند.
          مافیا اتاقِ پدرخوانده‌ی خودش را دارد. */}
      {room.game !== "mafia" && <TableRow carpet={room.carpet} host={host} />}

      {/* میزِ عمومی: میزبان روشن و خاموشش می‌کند، ولی **همه** می‌بینندش — کسی
          که سرِ میز می‌نشیند حق دارد بداند دارد پخش می‌شود. */}
      <div className="plate row" style={{ justifyContent: "space-between" }}>
        <span>{room.isPublic ? "میز عمومی — بقیه می‌توانند تماشا کنند" : "میز خصوصی"}</span>
        {host ? (
          <button className="chip" onClick={() => client.setPublic(!room.isPublic)}>
            {room.isPublic ? "خصوصی‌اش کن" : "عمومی‌اش کن"}
          </button>
        ) : (
          <span className="muted">انتخاب میزبان</span>
        )}
      </div>

      {/* چتِ میز: مثلِ «میزِ عمومی» انتخابِ میزبان است و دیده‌ی همه. وسطِ بازی
          هم می‌شود بستش — همان‌جا که معمولاً لازم می‌شود. */}
      <div className="plate row" style={{ justifyContent: "space-between" }}>
        <span>{room.chatOpen ? "چت میز باز است" : "چت میز بسته است"}</span>
        {host ? (
          <button className="chip" onClick={() => client.setChatOpen(!room.chatOpen)}>
            {room.chatOpen ? "ببندش" : "بازش کن"}
          </button>
        ) : (
          <span className="muted">انتخاب میزبان</span>
        )}
      </div>

      {/* قانون‌های میزبان: مهمان باید پیش از نشستن بداند با چه قانونی بازی می‌کند. */}
      {room.game === "marpele" && (
        <div className="plate row" style={{ justifyContent: "space-between" }}>
          <span>{["یک تاس", "دو تاس، انتخابی", "سریع — جمع دو تاس"][room.rules["dice"] ?? 0] ?? "یک تاس"}</span>
          <span className="muted">{(room.rules["magic"] ?? 1) === 1 ? "با خانه‌های جادویی" : "بی خانه‌ی جادویی"}</span>
        </div>
      )}

      {/* فلش‌ها: نقش و اندازه‌ای که میزبان برگزیده (قرعه: سرِ شروع). */}
      {room.game === "arrows" && (
        <div className="plate row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <span className="row" style={{ gap: 10, alignItems: "center" }}>
            {room.rules["mode"] !== 1 && (room.rules["pic"] ?? 0) > 0 && <Thumb pic={HANDMADE[(room.rules["pic"] ?? 1) - 1]![0]} className="ar-roomthumb" />}
            {arrowsRoomSummary(arrowsRoomFrom(room.rules))}
          </span>
          <span className="muted">
            {room.rules["mode"] === 1 ? "همه یک تیم"
              : room.rules["teams"] === 1 && players < 4 ? "تیمی فقط با ۴ نفر"
              : (room.rules["pic"] ?? 0) > 0 ? "انتخاب میزبان" : "سر شروع قرعه می‌شود"}
          </span>
        </div>
      )}

      {/* مافیا: سناریو و اندازه‌ی میز؛ نقش‌ها را سرور سرِ شروع قرعه می‌کشد. */}
      {room.game === "mafia" && (
        <div className="plate stack" style={{ gap: 4 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <span className="row" style={{ gap: 8, alignItems: "center" }}>
              <i className="scn-ico" aria-hidden="true"
                 style={{ "--ico": `url(/art/mafia/scenario/${(SCENARIOS[room.rules["scenario"] ?? 1] ?? SCENARIOS[1]!).key}.svg)` } as CSSProperties} />
              {(SCENARIOS[room.rules["scenario"] ?? 1] ?? SCENARIOS[1]!).fa}
            </span>
            <span className="muted">میز {fa(room.seats.length)} نفره</span>
          </div>
          <div className="muted">{(SCENARIOS[room.rules["scenario"] ?? 1] ?? SCENARIOS[1]!).desc}</div>
        </div>
      )}

      {/* منچ: قرصِ قانون‌ها — فقط آن‌چه با پیش‌فرض فرق دارد — و وضعِ «تیمی» با نشسته‌های فعلی. */}
      {mn && (
        <div className="plate stack" style={{ gap: 6 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <span>قانون‌ها</span>
            <span className="mn-pill">{menchPill(menchEffective(mn, players))}</span>
          </div>
          {mn.teams === 1 && players < 4 && <div className="muted">تیمی فقط با ۴ نفر؛ این بازی بدون تیم است</div>}
        </div>
      )}

      {/* ورودیِ میز: خواندنی، و برای مهمان مهم‌تر از میزبان — او باید پیش از
          نشستن بداند چقدر از کیفش کم می‌شود. */}
      {room.stake > 0n && (
        <div className="plate row" style={{ justifyContent: "space-between" }}>
          <span>ورودی میز: <b>{faNum(room.stake)} سکه</b></span>
          <span className="muted">۱۰٪ برش خانه</span>
        </div>
      )}

      {/* ربات سرِ میزِ ورودی‌دار می‌نشیند ولی پول نمی‌گذارد؛ میز تا وقتی
          دستِ‌کم دو آدم دارد ورودی‌دار می‌مانَد. میزبان باید پیش از زدنِ
          «شروع» بداند کدام حالت است — [[economy.StakeAllowed]]. */}
      {host && room.stake > 0n && filled < room.seats.length && (
        <div className="plate muted">
          {filled >= 2
            ? `${fa(room.seats.length - filled)} صندلی با ربات پر می‌شود. ربات ورودی نمی‌دهد و از گلدان هم چیزی نمی‌برد؛ ورودی فقط از ${fa(filled)} آدم سر میز گرفته می‌شود.`
            : "میز ورودی‌دار دست‌کم دو آدم می‌خواهد. با این تعداد، میز رایگان می‌شود و ورودی گرفته نمی‌شود."}
        </div>
      )}

      {host ? (
        <button className="btn" onClick={() => client.startRoom()}>
          شروع {bots > 0 ? `(${fa(bots)} ربات)` : board && filled < room.seats.length ? `با ${fa(filled)} نفر` : ""}
        </button>
      ) : (
        <div className="muted" style={{ textAlign: "center" }}>منتظر میزبان…</div>
      )}

      {partner && (
        <div className="muted" style={{ textAlign: "center" }}>
          یار تو: {partner.playerId ? partner.name || "بازیکن" : <BotIcon />}
        </div>
      )}

      <button className="btn ghost" onClick={() => client.leaveRoom()}>بیرون رفتن</button>
    </div>
  );
}

/**
 * چهره‌ی صندلیِ خالی یا رباتیِ اتاقِ تخته‌ای. ربات با پریدن می‌نشیند (همان لمس جوابش را می‌بیند)؛ میزبان
 * روی صندلیِ خالی «+» و روی ربات «×» می‌بیند — هر دو نشانه‌ی همان لمس‌اند، نه دکمه‌ی جدا.
 */
function BotSeatFace({ bot, host }: { bot: boolean; host: boolean }) {
  if (!bot) return <span className="seatface empty addbot" aria-hidden="true">+</span>;
  return (
    <span className="seatface botseat">
      <BotIcon size={20} />
      {host && <i className="botx" aria-hidden="true">×</i>}
    </span>
  );
}

/**
 * پیامِ دعوت: لینکِ مستقیمِ میز، نه کد (docs/friend-invite.md). دوست روی لینک می‌زند و سرِ همین
 * میز می‌نشیند — در مرورگر، یا در اپ اگر نصب است — و تلگرام از همین لینک کارتِ «… تو را به میزِ
 * حکم دعوت کرده» را می‌سازد. لینک آخرِ پیام است تا همان کارت شود.
 */
function TableRow({ carpet, host }: { carpet: string; host: boolean }) {
  const [open, setOpen] = useState(false);
  // کدام میزِ «میزهای من» را خودم فرستادم — اتاق فقط کلیدِ میز را برمی‌گرداند، نه شناسه را
  const [pick, setPick] = useState(() => (carpet ? loadTableChoice() : ""));
  useSignal(client.myTables);
  useEffect(() => { if (host && client.myTables.value === null) void client.loadMyTables(); }, [host]);
  // میزبان فقط میان میزهای خودش عوض می‌کند (ساختن در فروشگاه است)؛ بی میز و بی انتخاب، ردیفی نیست
  const canPick = host && hasTables();
  if (!carpet && !canPick) return null;
  return (
    <div className="plate stack" style={{ gap: 8 }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span>{carpet ? `میز ${tableName(carpet)}` : "میز: قرعه — سر شروع معلوم می‌شود"}</span>
        {canPick ? (
          <button className="chip" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? "بستن" : "عوض کن"}</button>
        ) : !host && (
          <span className="muted">انتخاب میزبان</span>
        )}
      </div>
      {canPick && open && (
        <TablePicker value={pick} onPick={(k) => { setPick(k); saveTableChoice(k); client.setCarpet(k); setOpen(false); }} />
      )}
    </div>
  );
}

/** پیامِ دعوت بی لینک — تلگرام لینک را جدا می‌گیرد و زیرِ متن می‌گذارد (مثلِ SocialShare.StripTail ِ اپ). */
export const INVITE_MSG = "بیا با هم «بازی وان» بازی کنیم! روی لینک بزن و مستقیم سر میزم بنشین — حساب هم لازم نیست:";

export function inviteText(url: string): string {
  return `${INVITE_MSG}\n${url}`;
}

/** لینکِ مستقیمِ میز — همان که لابی با ‎?room= می‌پیوندد (App.tsx). */
export function roomUrl(code: string, origin = location.origin): string {
  return `${origin}/play?room=${code}`;
}

/**
 * «لینکِ میز را بفرست» (کاربر، ۲۵ اکتبر ۲۰۲۶: «در این صفحه باید امکانِ اشتراکِ میز را داشته باشیم»): لینک دیده
 * می‌شود و با «کپی» (و خبرِ «کپی شد») برداشته می‌شود؛ تلگرام و واتس‌اپ با لینکِ رسمیِ وبشان، و «بیشتر» برگه‌ی
 * اشتراکِ سیستم اگر مرورگر دارد — همان کانال‌های SocialShare ِ اپ. پیش‌تر یک دکمه‌ی «هم‌رسانیِ کد» بود که روی
 * دسکتاپ بی‌صدا کپی می‌کرد و کسی نمی‌فهمید چه شد. گیرنده بی حساب هم می‌نشیند (Account.tsx، GuestSeat).
 */
function ShareTable({ code }: { code: string }) {
  const url = roomUrl(code);
  const text = inviteText(url);
  const [copied, setCopied] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    if (await copyText(text, field.current)) setCopied(true);
  };
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  return (
    <div className="stack roomshare">
      <b>لینک میز را برای دوستانت بفرست</b>
      <div className="muted">با همین لینک مستقیم سر میز می‌نشینند؛ حتی بدون حساب.</div>
      <div className="row rs-link">
        <input ref={field} readOnly value={url} dir="ltr" aria-label="لینک میز" onFocus={(e) => e.currentTarget.select()} />
        <button className="btn" onClick={() => void copy()} aria-live="polite">{copied ? "کپی شد ✓" : "کپی"}</button>
      </div>
      <div className="row rs-apps">
        <a className="btn rs-tg" target="_blank" rel="noopener noreferrer"
           href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(INVITE_MSG)}`}>تلگرام</a>
        <a className="btn rs-wa" target="_blank" rel="noopener noreferrer" href={`https://wa.me/?text=${encodeURIComponent(text)}`}>واتس‌اپ</a>
        {canShare && (
          <button className="btn ghost" onClick={() => void navigator.share({ text }).catch(() => { /* لغوِ کاربر */ })}>بیشتر…</button>
        )}
      </div>
    </div>
  );
}

/**
 * کپیِ متن: Clipboard API، وگرنه (صفحه‌ی نامطمئن، مرورگرِ کهنه) انتخابِ همان فیلد و execCommand. خروجی: کپی شد؟
 * فیلد پس از کپی همان لینک را نشان می‌دهد، نه متنِ کاملِ دعوت.
 */
async function copyText(text: string, field: HTMLInputElement | null): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* می‌رویم سراغِ راهِ قدیمی */ }
  if (!field) return false;
  const keep = field.value;
  try {
    field.value = text;
    field.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    field.value = keep;
  }
}
